/**
 * Общий поиск участков во внешней/архивной БД cadastre (не привязан к объекту ОИ).
 */
import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { cadastralApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Info } from "lucide-react";
import type {
  CadastreExternalParcelSummary,
  CadastreExternalHierarchyResponse,
  CadastreExternalSourceModeResponse,
  Geometry,
} from "@/types/api-v2";

const EXTERNAL_SEARCH_TIMEOUT_MS = 8_000;
const EXTERNAL_SEARCH_LONG_TIMEOUT_MS = 360_000;
const WEB_MERCATOR_MAX = 20_037_508.34;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    }),
  ]);
}

function mercatorToWgs84(coord: [number, number]): [number, number] {
  const [x, y] = coord;
  const lng = (x / 20037508.34) * 180;
  let lat = (y / 20037508.34) * 180;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
  return [lng, lat];
}

function isLikelyWebMercatorCoord(coord: unknown): coord is [number, number] {
  if (!Array.isArray(coord) || coord.length < 2) return false;
  const [x, y] = coord;
  if (typeof x !== "number" || typeof y !== "number") return false;
  return (
    Math.abs(x) <= WEB_MERCATOR_MAX &&
    Math.abs(y) <= WEB_MERCATOR_MAX &&
    (Math.abs(x) > 180 || Math.abs(y) > 90)
  );
}

function mapCoordinatesDeep(value: unknown, mapper: (coord: [number, number]) => [number, number]): unknown {
  if (!Array.isArray(value)) return value;
  if (isLikelyWebMercatorCoord(value)) return mapper(value);
  return value.map((item) => mapCoordinatesDeep(item, mapper));
}

function normalizeExternalGeoJson(geo: GeoJSON.Feature | GeoJSON.Geometry): GeoJSON.Feature | GeoJSON.Geometry {
  if (!geo) return geo;
  if (geo.type === "Feature") {
    const geometry = geo.geometry ? (normalizeExternalGeoJson(geo.geometry) as GeoJSON.Geometry) : geo.geometry;
    return { ...geo, geometry };
  }
  if (geo.type === "GeometryCollection") {
    return {
      ...geo,
      geometries: geo.geometries.map((g) => normalizeExternalGeoJson(g) as GeoJSON.Geometry),
    };
  }
  if ("coordinates" in geo) {
    return {
      ...geo,
      coordinates: mapCoordinatesDeep(geo.coordinates, mercatorToWgs84) as GeoJSON.Position | GeoJSON.Position[] | GeoJSON.Position[][] | GeoJSON.Position[][][],
    } as GeoJSON.Geometry;
  }
  return geo;
}

export interface CadastreExternalSearchFormProps {
  /** Компактный layout для правой панели (кнопки под полем). */
  compact?: boolean;
}

export function CadastreExternalSearchForm({ compact = false }: CadastreExternalSearchFormProps) {
  const { toast } = useToast();
  const [externalCadNum, setExternalCadNum] = useState("");
  const [externalSearchLimit, setExternalSearchLimit] = useState(20);
  const [externalSearchItems, setExternalSearchItems] = useState<CadastreExternalParcelSummary[]>([]);
  const [externalParcel, setExternalParcel] = useState<CadastreExternalParcelSummary | null>(null);
  const [externalHierarchy, setExternalHierarchy] = useState<CadastreExternalHierarchyResponse | null>(null);
  const [externalSearchStatus, setExternalSearchStatus] = useState<
    "idle" | "searching" | "timeout" | "cancelled" | "error" | "success"
  >("idle");
  const [externalSearchMode, setExternalSearchMode] = useState<"normal" | "long">("normal");
  const [lastResultSource, setLastResultSource] = useState<string | null>(null);
  const activeSearchTokenRef = useRef(0);

  const { data: cadastreSource } = useQuery<CadastreExternalSourceModeResponse>({
    queryKey: ["cadastral", "external", "source-mode"],
    queryFn: () => cadastralApi.externalSourceMode(),
    staleTime: 60_000,
    retry: false,
  });

  const sourceMode = (cadastreSource?.sourceMode || "").toLowerCase();
  const sourceModeLabel = (() => {
    if (sourceMode === "legacy") return "API / НСПД";
    if (sourceMode === "external") return "Архив БД";
    if (sourceMode === "hybrid") return "Гибрид (архив + НСПД)";
    return cadastreSource?.sourceMode || "—";
  })();

  const resultSourceLabel = (source?: string | null) => {
    const s = (source || "").toLowerCase();
    if (s === "archive") return "Архив";
    if (s === "api") return "НСПД";
    if (s === "api_degraded") return "НСПД (архив недоступен)";
    return null;
  };

  const isFullCadNum = (value: string) => /^\d{2}:\d{2}:\d{6,7}:\d+$/.test(value.trim());

  const searchExternalMutation = useMutation({
    mutationFn: async ({ timeoutMs, mode }: { timeoutMs: number; mode: "normal" | "long" }) => {
      const searchToken = ++activeSearchTokenRef.current;
      setExternalSearchMode(mode);
      setExternalSearchStatus("searching");
      const search = await withTimeout(
        cadastralApi.externalSearch(externalCadNum.trim(), externalSearchLimit),
        timeoutMs,
        mode === "long"
          ? "Расширенный поиск выполняется слишком долго. Попробуйте уточнить кадастровый номер."
          : "Поиск выполняется слишком долго. Уточните кадастровый номер.",
      );
      if (searchToken !== activeSearchTokenRef.current) {
        throw new Error("SEARCH_CANCELLED");
      }
      if (!search.items.length) {
        return { items: [], parcel: null, hierarchy: null, source: search.source || "none" };
      }
      const parcel = await cadastralApi.externalGetParcel(search.items[0].cadNum);
      let hierarchy: CadastreExternalHierarchyResponse | null = null;
      if (parcel.pkkId) {
        try {
          hierarchy = await cadastralApi.externalGetHierarchy(parcel.pkkId);
        } catch {
          hierarchy = null;
        }
      }
      if (searchToken !== activeSearchTokenRef.current) {
        throw new Error("SEARCH_CANCELLED");
      }
      return {
        items: search.items,
        parcel,
        hierarchy,
        source: parcel.source || search.source || "none",
      };
    },
    onSuccess: ({ items, parcel, hierarchy, source }) => {
      setExternalSearchItems(items);
      setExternalParcel(parcel);
      setExternalHierarchy(hierarchy);
      setExternalSearchStatus("success");
      setLastResultSource(source);
      if (!parcel) {
        const query = externalCadNum.trim();
        const shortPrefix = !isFullCadNum(query);
        toast({
          title: "Ничего не найдено",
          description: shortPrefix
            ? "Для короткого префикса нужен архив БД или укажите полный кадастровый номер (НСПД)."
            : "По указанному номеру нет данных в доступных источниках (архив / НСПД).",
        });
      }
    },
    onError: (error: unknown) => {
      const message = String((error as { message?: string })?.message || "");
      if (message === "SEARCH_CANCELLED") {
        setExternalSearchStatus("cancelled");
        return;
      }
      if (message.includes("слишком долго")) {
        setExternalSearchStatus("timeout");
        toast({
          title: externalSearchMode === "long" ? "Расширенный поиск слишком долгий" : "Поиск выполняется слишком долго",
          description:
            externalSearchMode === "long"
              ? "Даже расширенный поиск не успел завершиться. Уточните кадастровый номер и попробуйте снова."
              : "Уточните кадастровый номер (например, добавьте квартал/участок) и повторите запрос.",
          variant: "destructive",
        });
        return;
      }
      setExternalSearchStatus("error");
      toast({
        title: "Ошибка внешнего поиска",
        description: message || "Не удалось выполнить поиск во внешней БД cadastre",
        variant: "destructive",
      });
    },
  });

  const selectExternalParcelMutation = useMutation({
    mutationFn: async (cadNum: string) => {
      const parcel = await cadastralApi.externalGetParcel(cadNum);
      let hierarchy: CadastreExternalHierarchyResponse | null = null;
      if (parcel.pkkId) {
        try {
          hierarchy = await cadastralApi.externalGetHierarchy(parcel.pkkId);
        } catch {
          hierarchy = null;
        }
      }
      return { parcel, hierarchy };
    },
    onSuccess: ({ parcel, hierarchy }) => {
      setExternalParcel(parcel);
      setExternalHierarchy(hierarchy);
      if (parcel.source) setLastResultSource(parcel.source);
    },
    onError: (error: unknown) => {
      toast({
        title: "Ошибка получения участка",
        description: (error as { message?: string })?.message || "Не удалось получить данные найденного участка",
        variant: "destructive",
      });
    },
  });

  const handleFocusExternalGeometry = () => {
    const geom = externalParcel?.geom;
    if (!geom) {
      toast({
        title: "Нет геометрии",
        description: "Для этого участка геометрия отсутствует в ответе источника",
        variant: "destructive",
      });
      return;
    }
    const focusedGeometry: Geometry = {
      id: -1,
      object_id: -1,
      name: `cadastre:${externalParcel?.cadNum ?? "external"}`,
      geojson: normalizeExternalGeoJson(geom as GeoJSON.Feature | GeoJSON.Geometry),
      type: "Polygon",
      style: {},
      created_at: new Date().toISOString(),
    };
    window.dispatchEvent(
      new CustomEvent("focusGeometryOnMap", {
        detail: { geometry: focusedGeometry, source: "cadastral" },
      }),
    );
    toast({
      title: "Геометрия отправлена на карту",
      description: "Карта центрирована на найденном участке",
    });
  };

  return (
    <Card className={compact ? "border-0 shadow-none" : undefined}>
      <CardHeader className={compact ? "space-y-2 px-0 pt-0" : "space-y-2"}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="text-base">Поиск кадастров</CardTitle>
          <div className="flex flex-wrap gap-1.5 justify-end">
            <Badge variant="secondary" className="shrink-0">
              Режим: {sourceModeLabel}
            </Badge>
            {resultSourceLabel(lastResultSource) ? (
              <Badge variant="outline" className="shrink-0">
                Источник: {resultSourceLabel(lastResultSource)}
              </Badge>
            ) : null}
          </div>
        </div>
        <CardDescription>
          {sourceMode === "legacy"
            ? "Поиск по полному кадастровому номеру через публичный реестр (НСПД)."
            : sourceMode === "hybrid"
              ? "Сначала архив БД cadastre, при отсутствии — НСПД для полного номера."
              : "Данные из архивной БД cadastre; при недоступности архива полный номер может искаться в НСПД."}
        </CardDescription>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>
            {sourceMode === "external" && lastResultSource !== "api_degraded" && lastResultSource !== "api"
              ? "Архивный источник"
              : "Справочный поиск"}
          </AlertTitle>
          <AlertDescription>
            {sourceMode === "external" && lastResultSource !== "api_degraded" && lastResultSource !== "api"
              ? "Выдача отражает состояние данных на момент выгрузки в cadastre, а не «живой» кадастр. Для юридически значимых сверок используйте официальные сервисы Росреестра."
              : "Результат носит справочный характер (архив и/или НСПД). Актуальность и полнота не гарантируются; для юридически значимых сверок — официальные сервисы Росреестра."}
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent className={`space-y-3 ${compact ? "px-0 pb-0" : ""}`}>
        <div className={compact ? "flex flex-col gap-2" : "flex gap-2"}>
          <Input
            placeholder="Кадастровый номер, например 77:01:0004010:1234"
            value={externalCadNum}
            onChange={(e) => {
              if (searchExternalMutation.isPending) {
                activeSearchTokenRef.current += 1;
                setExternalSearchStatus("cancelled");
              }
              setExternalCadNum(e.target.value);
              setExternalSearchLimit(20);
              setExternalSearchItems([]);
              setLastResultSource(null);
            }}
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              className={compact ? "flex-1" : undefined}
              onClick={() =>
                searchExternalMutation.mutate({
                  timeoutMs: EXTERNAL_SEARCH_TIMEOUT_MS,
                  mode: "normal",
                })
              }
              disabled={!externalCadNum.trim() || searchExternalMutation.isPending}
            >
              {searchExternalMutation.isPending ? "Поиск..." : "Найти"}
            </Button>
            <Button
              variant="secondary"
              className={compact ? "flex-1" : undefined}
              onClick={() =>
                searchExternalMutation.mutate({
                  timeoutMs: EXTERNAL_SEARCH_LONG_TIMEOUT_MS,
                  mode: "long",
                })
              }
              disabled={!externalCadNum.trim() || searchExternalMutation.isPending}
            >
              {searchExternalMutation.isPending ? "Поиск..." : "Искать дольше"}
            </Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Короткий префикс (например, `77:01`) ищет только в архиве — без архива укажите полный номер для НСПД.
          Для длинного префикс-поиска в архиве используйте «Искать дольше».
        </div>
        {externalSearchItems.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              Найдено: {externalSearchItems.length}
              {externalSearchItems.length >= externalSearchLimit && externalSearchLimit < 200
                ? " (можно догрузить)"
                : ""}
            </div>
            <div className="max-h-36 overflow-y-auto rounded border">
              {externalSearchItems.map((item) => (
                <button
                  key={`${item.gid}-${item.cadNum}`}
                  className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 hover:bg-muted/40 ${
                    externalParcel?.cadNum === item.cadNum ? "bg-muted/60" : ""
                  }`}
                  onClick={() => selectExternalParcelMutation.mutate(item.cadNum)}
                  type="button"
                >
                  <div className="font-medium">{item.cadNum}</div>
                  <div className="text-xs text-muted-foreground">PKK ID: {item.pkkId || "—"}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const nextLimit = Math.min(200, externalSearchLimit + 20);
                  setExternalSearchLimit(nextLimit);
                  setTimeout(
                    () =>
                      searchExternalMutation.mutate({
                        timeoutMs:
                          externalSearchMode === "long"
                            ? EXTERNAL_SEARCH_LONG_TIMEOUT_MS
                            : EXTERNAL_SEARCH_TIMEOUT_MS,
                        mode: externalSearchMode,
                      }),
                    0,
                  );
                }}
                disabled={externalSearchLimit >= 200 || searchExternalMutation.isPending}
              >
                Показать еще
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              {externalSearchStatus === "searching"
                ? externalSearchMode === "long"
                  ? "Выполняется расширенный поиск (до 60 секунд)..."
                  : "Поиск выполняется (до 8 секунд)..."
                : null}
              {externalSearchStatus === "timeout"
                ? externalSearchMode === "long"
                  ? "Расширенный поиск прерван по таймауту. Уточните номер и повторите запрос."
                  : "Поиск прерван по таймауту. Уточните номер и попробуйте снова."
                : null}
              {externalSearchStatus === "cancelled" ? "Предыдущий поиск отменен." : null}
              {externalSearchStatus === "error"
                ? "Поиск завершился ошибкой. Проверьте доступность источника."
                : null}
            </div>
          </div>
        ) : null}
        {externalParcel && (
          <div className="text-sm space-y-1">
            <div>
              <span className="font-medium">Номер:</span> {externalParcel.cadNum}
            </div>
            <div>
              <span className="font-medium">PKK ID:</span> {externalParcel.pkkId}
            </div>
            {externalParcel.area ? (
              <div>
                <span className="font-medium">Площадь:</span> {externalParcel.area}
              </div>
            ) : null}
            <div className="pt-1">
              <Button variant="outline" size="sm" onClick={handleFocusExternalGeometry}>
                <MapPin className="h-4 w-4 mr-2" />
                Перейти на карту
              </Button>
            </div>
            {externalHierarchy?.kvartal?.cadNum ? (
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>
                  Иерархия: {externalHierarchy.okrug?.cadNum || "-"} / {externalHierarchy.rayon?.cadNum || "-"} /{" "}
                  {externalHierarchy.kvartal?.cadNum || "-"}
                </div>
                {externalHierarchy.kvartal?.name ? (
                  <div className="line-clamp-2" title={externalHierarchy.kvartal.name}>
                    Квартал: {externalHierarchy.kvartal.name}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
